import {Calculator, ShowErrorWay} from "../calculator";
import {DateField} from "../datetime_fields.js";
import {SummaryField} from "../../components/summary_field/summary_field.js";
import {PeriodicDateField} from "../../components/periodic_date_field/periodic_date_field.js";
import {DatePoint} from '../../js/datelib.js';

class BaseCreditCalculator extends Calculator {
    initialize() {
        const self = this;
        this.patchFieldTypes([DateField, SummaryField, PeriodicDateField]);

        self.todayDate = new DatePoint();

        // add input fields
        this.addInputField('#date-from', 'dateFrom', {
            errors: [
                {check: function (v) {return v.valid;}, selector: '#date-from-err .null-error'},
                {check: function (v) {return !v.valid || v.date.getFullYear() > 1994;}, selector: '#date-from-err .from-bound-err'}
            ],
            unsilence: ['changingRate[].date', 'earlyRepayment[].summary']
        });

        this.addInputField('#credit-term .base-input', 'creditTerm', {
            type: 'count',
            errors: [{check: function (v) {return !!v;}, selector: '#credit-term .null-error'}],
            unsilence: ['earlyRepayment[].summary']
        });

        this.creditRateField = this.addInputField('#credit-rate .fixed-rate', 'creditRate', {
            type: 'float', options: {precision: 4},
            errors: [{check: function (v) {return v != null;}, selector: '#credit-rate .null-error'}]
        });

        // ie11 fix for a proper creation of <dropdown-select> inside <periodic-select>
        if (document.documentMode) {
            const $month = $("#frequency-month");
            const monthPeriodic = $month[0].querySelector("periodic-select");
            const monthClone = monthPeriodic.cloneNode(true);
            $month[0].replaceChild(monthClone, monthPeriodic);
        }

        this.frequencyMonthField = this.addInputField('#frequency-month periodic-select', 'frequencyMonth', {
            type: 'periodic',
        });

        this.firstChangingRateField = this.addInputField('#changing-rate-table .static-row .dynamic-list-rate-percent .base-input', 'firstChangingRate', {
            type: 'float', options: {precision: 4},
            errors: [{check: function (v) {return v != null;}, selector: '#changing-rate-table .static-row .dynamic-row-error-rate'}]
        });
        const $staticRow = $('#changing-rate-table .static-row');
        $staticRow.find('.dynamic-list-row-remove a').on("click", (e) => {
            e.preventDefault();
            const $input = $staticRow.find(".dynamic-list-rate-percent input.base-input");
            $input.val("");
            $input.removeClass("err");
            $staticRow.find(".error-text-block .error-text").removeAttr("style");
            $(e.target).trigger("blur");
        });

        // add group and dynamic fields
        this.changingRateField = this.addInputField('#changing-rate-table', 'changingRate', {
            dynamic: true,
            appendButtonSelector: "#addRate",
            removeSelector: ".dynamic-list-row-remove a",
            keepTable: true,
            ignoreForEmpty: false,
            dontSubmitOnRemove: true,
            afterRemoval: function () {
                const $date = $("#changing-rate-table").find(".dynamic-list-row").find("date-picker");
                self.submitForm($date[0] || null, !self._submitOnChange);
            },
            fields: {
                date: {
                    selector: ".dynamic-list-rate-date date-picker",
                    options: function ($clone) {
                        return {
                            showErrorWay: ShowErrorWay.SHOW_FIRST,
                            errors: [
                                {
                                    check: function (v, autoValidation) {return (autoValidation && $clone.find(".dynamic-list-rate-percent input").val() === '') || v.valid;},
                                    selector: $clone.find('.dynamic-row-error-date')
                                },
                                {check: function (v) {return !v.goesBefore(new DatePoint("01.01.1995"));}, selector: $clone.find('.from-bound-err')},
                                {check: function (v) {return !self.dateFrom || !v.goesBefore(self.dateFrom);}, selector: $clone.find('.before-start-err')},
                                {
                                    check: function (v) {
                                        return !v.valid || !(self.dateFrom && self.dateFrom.distanceTo(v) == 1) && !(self.changingRate
                                            && self.changingRate.filter(r => r.date && r.date.distanceTo(v) == 1).length);
                                    },
                                    selector: $clone.find('.double-error')
                                }
                            ],
                            unsilence: ['changingRate[].date']
                        }
                    },
                },
                rate: {
                    selector: ".dynamic-list-rate-percent input",
                    options: function ($clone) {
                        return {
                            type: 'float',
                            options: {precision: 4},
                            errors: [{check: function (v) {return v != null;}, selector: $clone.find('.dynamic-row-error-rate')}]
                        }
                    },
                },
            }
        });

        this.earlyRepaymentField = this.addInputField('#early-repayment-table', 'earlyRepayment', {
            dynamic: true,
            appendButtonSelector: "#addRepayment",
            removeSelector: ".dynamic-list-row-remove a",
            keepOne: true,
            removeFirstEvent: function () {
                const $list = $("#early-repayment-table .dynamic-list-row");
                const $input = $list.find("summary-input");
                $input.val("");
                $list.removeClass("err");
                $($input[0].shadowRoot).find(".error-text").removeAttr("style");
            },
            ignoreForEmpty: false,
            fields: {
                summary: {
                    selector: "summary-input",
                    options: function ($clone) {
                        return {
                            type: 'summary',
                            showErrorWay: ShowErrorWay.SHOW_FIRST,
                            errors: SummaryField.getSummaryErrors($clone, self, `dateFrom`, `dateTo`),
                            errClassElem: $clone
                        }
                    },
                },
            }
        });

        this.firstChangingRateField.ignore = true;
        this.changingRateField.ignore = true;
        this.setDynamicInputIgnore('changingRate', true);
        this.earlyRepaymentField.ignore = true;
        this.setDynamicInputIgnore('earlyRepayment', true);

        // add output fields
        this.addOutputField('#credit-rate-out', 'creditRateOut', {type: 'percent', minPrecision: 0, maxPrecision: 4});
        this.addOutputField('#changing-rate-out', 'changingRateOut', {
            html: true,
            render: (r) => `Ставка кредита: ` + r.map(period =>
                period.rate != null ? `с ${period.from} – ${period.rate.toLocaleString('ru-RU', {maximumFractionDigits: 4})}% в год` : ''
            ).join(`, `)
        });
        this.addOutputField('.date-to-render', 'dateTo', {type: 'dateToRender', render: function (r) {return r ? r.toString() : null}});
        this.addOutputField('.closure-date-render', 'closureDate', {type: 'closureDateRender', render: function (r) {return r ? r.toString() : null}});

        this.addOutputField('#detalization', 'detalization', {
            html: true,
            render: (r) => {
                const table = document.createElement('table');
                table.classList.add('output-table');
                const header = `<tr class="output-table-header-row"><td>дата</td><td>платёж</td><td>основн.&nbsp;долг</td><td>проценты</td><td>остаток</td></tr>`;
                const trs = r.map(row => {
                    switch (row.type) {
                        case "payment": {
                            return `<tr class="output-table-add-row"><td class="output-table-add-row-column__left">${row.date}</td>` +
                                `<td class="output-table-add-row-column__left">${row.payment.toLocaleString('ru-RU', {minimumFractionDigits: 2})}</td>` +
                                `<td class="output-table-add-row-column__right">${row.debt.toLocaleString('ru-RU', {minimumFractionDigits: 2})}</td>` +
                                `<td class="output-table-add-row-column__right">${row.percents.toLocaleString('ru-RU', {minimumFractionDigits: 2})}</td>` +
                                `<td class="output-table-add-row-column__right">${row.remains.toLocaleString('ru-RU', {minimumFractionDigits: 2})}</td></tr>`;
                        }
                        case "repayment": {
                            return `<tr class="output-table-add-row"><td class="output-table-add-row-column__left">${row.date}</td>` +
                                `<td></td>` +
                                `<td class="output-table-add-row-column__right">${row.debt.toLocaleString('ru-RU', {minimumFractionDigits: 2})}</td>` +
                                `<td class="output-table-add-row-column__right">${row.percents.toLocaleString('ru-RU', {minimumFractionDigits: 2})}</td>` +
                                `<td class="output-table-add-row-column__right">${row.remains.toLocaleString('ru-RU', {minimumFractionDigits: 2})}</td></tr>` +
                                `<tr class="output-table-add-row"><td colspan="5" class="output-table-add-row-column__left"><p>Досрочное погашение</p>` +
                                `<p>${(row.purpose == "sum" ? 'уменьшение суммы платежа' : 'уменьшение срока кредита')}</p></td></tr>` +
                                `<tr class="output-table-add-row"><td colspan="5" class="output-table-add-row-column__left"><span class="repayment-color">${(-row.payment).toLocaleString('ru-RU', {minimumFractionDigits: 2})}</span>` +
                                `${row.name ? `<span class="name">${row.name}</span>` : ``}</td></tr>`;
                        }
                        default: {
                            return ``;
                        }
                    }
                });
                const inner = header + trs.join(``);
                table.innerHTML = inner;
                return table.outerHTML;
            }
        });

        this.loadDescription();

        this.submitOnClick('#calcSubmit');
        this.addResetButton("#clearCalcInput");
    }

    submitOnClick(selectorOrElem) {
        var self = this;
        $(selectorOrElem).on("click", function () {
            $(`summary-input`).each((i, el) => {el.closeWindow();});
            self.submitForm(null, false, true);
        });
    }

    hideResults() {
        super.hideResults();
        this.sendStatisticMessage('Is_Made_With_Error');
    }

    renderError() {
        super.renderError();
        this.sendStatisticMessage('Is_Made_With_Error');
    }

    onReset() {
        $('.calc-div-results').removeClass('show-results');

        const $staticRow = $('#changing-rate-table .static-row');
        const $datePlaceholder = $staticRow.find('.date-placeholder');
        const $dateDisplay = $staticRow.find('.date-display');
        const $rateType = $("#credit-rate > .typed-value > .field-div > dropdown-select");
        const $creditRate = $("#credit-rate .input-with-error");
        const $changingRate = $("#changing-rate");
        const $earlyRepaymentInner = $("#early-repayment-inner");
        const $frequencyMonth = $("#frequency-month");

        $datePlaceholder.removeClass('hidden-control');
        $dateDisplay.addClass('hidden-control');
        $dateDisplay.text(``);
        $rateType.addClass('fixed');
        $rateType.attr('kind', "sharp-right");
        $creditRate.removeClass('hidden-control');
        $changingRate.addClass('hidden-control');
        $earlyRepaymentInner.addClass('hidden-control');
        $frequencyMonth.removeClass('hidden-control');

        this.creditRateField.ignore = false;
        this.firstChangingRateField.ignore = true;
        this.changingRateField.ignore = true;
        this.setDynamicInputIgnore('changingRate', true);
        this.earlyRepaymentField.ignore = true;
        this.setDynamicInputIgnore('earlyRepayment', true);
        this.frequencyMonthField.ignore = false;
    }

    generateExportObject() {
        const result = super.generateExportObject();

        result.manual = {};
        result.manual.finalPayment = this.finalPayment;
        result.manual.minMonthSum = this.minMonthSum;
        result.manual.maxMonthSum = this.maxMonthSum;
        result.manual.totalSum = this.totalSum;
        result.manual.overpayment = this.overpayment;
        result.manual.isRepaymentExcess = this.isRepaymentExcess;
        result.manual.isClosure = this.isClosure;
        result.manual.hasExcludedRepayments = this.hasExcludedRepayments;
        result.manual.excludedRepayments = this.excludedRepayments;
        result.manual.excludedRepaymentMessage = this.excludedRepaymentMessage;
        result.manual.repaymentTotal = this.repaymentTotal;
        result.manual.lastRepayment = this.lastRepayment;
        result.manual.previousSum = this.previousSum;
        result.manual.detalization = tools.deepcopy(this.detalization);
        result.manual.changingRateOut = tools.deepcopy(this.changingRateOut);
        result.manual.rateTypeValue = this.rateType.value;
        result.manual.orderTypeValue = this.orderType.value;

        return result;
    }

    showResults() {
        super.showResults();

        var goalParams = {
            args: {
            },
            message: 'Is_Made'
        };
        this.sendStatisticMessage(goalParams);
    }

    afterParse(sender, autoValidation) {
        const $elem = $(sender);
        const $dateFrom = $("#date-from");
        const $rateType = $("#credit-rate > .typed-value > .field-div > dropdown-select");
        const $creditRate = $("#credit-rate .input-with-error");
        const $changingRate = $("#changing-rate");
        const $earlyRepaymentCheckbox = $("#early-repayment > .input-container-v1 > .checkboxrow");
        const $earlyRepaymentInner = $("#early-repayment-inner");

        if ($elem.is($dateFrom)) {
            const $staticRow = $('#changing-rate-table .static-row');
            const $datePlaceholder = $staticRow.find('.date-placeholder');
            const $dateDisplay = $staticRow.find('.date-display');
            if (this.dateFrom) {
                $datePlaceholder.addClass('hidden-control');
                $dateDisplay.removeClass('hidden-control');
                $dateDisplay.text(this.dateFrom.toString());
            } else {
                $datePlaceholder.removeClass('hidden-control');
                $dateDisplay.addClass('hidden-control');
                $dateDisplay.text(``);
            }
        }

        if ($elem.is($rateType)) {
            if (this.rateType.value == "changing") {
                $creditRate.addClass('hidden-control');
                $changingRate.removeClass('hidden-control');
                this.creditRateField.ignore = true;
                this.firstChangingRateField.ignore = false;
                this.changingRateField.ignore = false;
                this.setDynamicInputIgnore('changingRate', false);
                $rateType.removeClass('fixed');
                $rateType.attr('kind', "");
            } else {
                $creditRate.removeClass('hidden-control');
                $changingRate.addClass('hidden-control');
                this.creditRateField.ignore = false;
                this.firstChangingRateField.ignore = true;
                this.changingRateField.ignore = true;
                this.setDynamicInputIgnore('changingRate', true);
                $rateType.addClass('fixed');
                $rateType.attr('kind', "sharp-right");
            }
        }

        if ($elem.is($earlyRepaymentCheckbox)) {
            if (this.isEarlyRepayment) {
                $earlyRepaymentInner.removeClass('hidden-control');
                this.earlyRepaymentField.ignore = false;
                this.setDynamicInputIgnore('earlyRepayment', false);
            } else {
                $earlyRepaymentInner.addClass('hidden-control');
                this.earlyRepaymentField.ignore = true;
                this.setDynamicInputIgnore('earlyRepayment', true);
            }
        }

        // Combined error for changingRate
        function proceedTwoErrors($error1, $error2, $error3, $error4) {
            if ($error4[0]) {
                $error2.css('display', 'block');
                $error3.removeAttr("style");
                if ($error4[0].innerHTML.slice(-1) != ';')
                    $error4[0].innerHTML += ';';
            } else {
                $error1.removeAttr("style");
                $error2.removeAttr("style");
                $error3.css('display', 'block');
            }
        }
        function updateChangingRateFields(tableSelector, Input1DivSelector, Input2DivSelector, err1Selector, err2Selector, err3Selector) {
            $(tableSelector).find(".dynamic-list-row").each((i, elem) => {
                const $elem = $(elem);
                const $firstInput = $elem.find(`${Input1DivSelector} .base-input`);
                const $secondInput = $elem.find(`${Input2DivSelector} .base-input`);
                const firstHasErr = $firstInput.hasClass('err');
                const secondHasErr = $secondInput.hasClass('err');
                const $error1 = $elem.find(err1Selector);
                const $error2 = $elem.find(err2Selector);
                const $error3 = $elem.find(err3Selector);
                const $error4 = $elem.find('.error-text:visible').filter((i, el) => [$error1[0], $error2[0], $error3[0]].indexOf(el) == -1);
                if (firstHasErr && secondHasErr) {
                    $firstInput.attr('kind', "s-small transparent grip-right");
                    $secondInput.addClass("sharp-left");
                    proceedTwoErrors($error1, $error2, $error3, $error4)
                } else {
                    $firstInput.attr('kind', "s-small transparent");
                    $secondInput.removeClass("sharp-left");
                    $error3.removeAttr("style");
                    if (secondHasErr)
                        $error2.css('display', 'block');
                    if (firstHasErr) {
                        if ($error2.css('display') == 'none') {
                            if ($error4[0]) {
                                if ($error4[0].innerHTML.slice(-1) == ';')
                                    $error4[0].innerHTML = $error4[0].innerHTML.slice(0, -1);
                            } else
                                $error1.css('display', 'block');
                        } else
                            proceedTwoErrors($error1, $error2, $error3, $error4);
                    }
                }
            });
        }

        updateChangingRateFields('#changing-rate', '.dynamic-list-rate-date', '.dynamic-list-rate-percent', '.dynamic-row-error-date', '.dynamic-row-error-rate', '.error-date-rate');

        const prevDateTo = this.dateTo;

        if (this.dateFrom && this.creditTerm) {
            const termMonths = Math.ceil(this.creditTerm) * (this.termType.value == 1 ? 12 : 1);
            const isFullMonth = this.getIsFullMonth(this.dateFrom, this.frequencyMonth);
            const isFullFp = isFullMonth && this.fpType.value == 1;
            const payments = this.getPaymentDateCalendar(this.dateFrom, termMonths, this.frequencyMonth, isFullFp, isFullMonth, this.onlyWorkdays);
            this.dateTo = payments[payments.length - 1].date;
        } else {
            delete this.dateTo;
        }

        if ((prevDateTo ? !this.dateTo || prevDateTo.distanceTo(this.dateTo) !== 1 : !!this.dateTo))
            this.parse($("#credit-term .base-input")[0], autoValidation);
    }

    calculate(creditSum) {
        const self = this;
        // Calculation are done in kopeykas/cents (objects with the postfix 'C' are for cents)
        const creditSumC = creditSum * 100;
        const termMonths = Math.ceil(this.creditTerm) * (this.termType.value == 1 ? 12 : 1);
        const isTypeAnnuity = this.orderType.value == 'type-annuity';
        const repayments = this.isEarlyRepayment && this.earlyRepayment ? this.earlyRepayment.map(r => r.summary).filter(s => !s.defaultInputs) : [];
        const changingRate = [{date: this.dateFrom, rate: this.firstChangingRate}];
        if (this.changingRate)
            changingRate.push(...this.changingRate);

        const isFullMonth = this.getIsFullMonth(this.dateFrom, this.frequencyMonth);
        const isFullFp = isFullMonth && this.fpType.value == 1;

        const calendarsC = {rateChanges: [], payments: [], repayments: []};
        calendarsC.rateChanges = getRateChangesCalendar(this.dateFrom, this.rateType.value, this.creditRate, changingRate);
        calendarsC.payments = this.getPaymentDateCalendar(this.dateFrom, termMonths, this.frequencyMonth, isFullFp, isFullMonth, this.onlyWorkdays);
        calendarsC.repayments = getAdditionalPaymentCalendar(this.dateFrom, repayments, calendarsC.payments, true);
        this.dateTo = calendarsC.payments[calendarsC.payments.length - 1].date;

        // Refills calendarsC.payments with full payment data
        let {finalTerm, excess} = calculatePayments(creditSumC, calendarsC, termMonths, isTypeAnnuity, isFullFp);

        if (!isFullFp && finalTerm > 1)
            finalTerm -= 1;

        const detalizationC = [...calendarsC.payments, ...calendarsC.repayments].sort((a, b) => {
            let order = b.date.distanceTo(a.date) - 1;
            if (!order)
                order = b.realDate.distanceTo(a.realDate) - 1;
            if (!order) {
                if (a.type == "repayment")
                    order += 0.2;
                if (b.type == "repayment")
                    order -= 0.2;
            }
            return order;
        });

        if (!detalizationC[detalizationC.length - 1].payment)
            detalizationC.pop();

        let totalSumC = 0;
        let repaymentTotalC = 0;
        detalizationC.forEach(row => {
            switch (row.type) {
                case "payment": {
                    totalSumC += row.payment;
                    break;
                }
                case "repayment": {
                    repaymentTotalC += row.payment;
                    break;
                }
                default: {
                    break;
                }
            }
        });
        totalSumC += repaymentTotalC;
        const overpaymentC = totalSumC - creditSumC;

        let detalizationCCopy = [...detalizationC];
        const fork = [];
        let stopSkipping = false;
        if (!isFullMonth) {
            const payment = detalizationCCopy.find(r => r.type == "payment");
            if (payment)
                payment.toNextMonth = true;
        }
        while (detalizationCCopy.length) {
            let month = detalizationCCopy[0].realDate.date.getMonth();
            let year = detalizationCCopy[0].realDate.date.getFullYear();
            if (detalizationCCopy[0].toNextMonth) {
                month = detalizationCCopy[0].date.date.getMonth();
                year = detalizationCCopy[0].date.date.getFullYear();
            }
            const detalizationCPeriod = detalizationCCopy.filter(r =>
                (!r.toNextMonth && (r.realDate.date.getMonth() == month)
                    && r.realDate.date.getFullYear() == year) ||
                (r.toNextMonth && (r.date.date.getMonth() == month)
                    && r.date.date.getFullYear() == year));
            detalizationCCopy = detalizationCCopy.slice(detalizationCPeriod.length);
            if (!isFullFp && isFullMonth && !stopSkipping && detalizationCPeriod.reduce((total, row) => total + row.debt, 0) == 0)
                continue;
            else
                stopSkipping = true;
            if (detalizationCCopy.length && !detalizationCPeriod.some(row => row.type == "payment"))
                continue;
            const payment = detalizationCPeriod.reduce((total, row) => total + row.payment, 0);
            fork.push(payment);
        }
        const finalPaymentC = fork.length ? fork.pop() : 0;
        const minMonthSumC = fork.length ? Math.min(...fork) : 0;
        const maxMonthSumC = fork.length ? Math.max(...fork) : 0;
        const lastPaymentC = detalizationC.slice(-1)[0];

        //set outputs
        this.finalPayment = finalPaymentC / 100;
        this.minMonthSum = minMonthSumC / 100;
        this.maxMonthSum = maxMonthSumC / 100;
        this.totalSum = totalSumC / 100;
        this.overpayment = overpaymentC / 100;
        this.overpaymentPercent = overpaymentC * 100 / creditSumC;
        this.isRepaymentExcess = excess.isRepaymentExcess;
        this.isClosure = excess.isClosure;
        this.lastRepayment = excess.lastRepaymentC / 100;
        this.previousSum = lastPaymentC.payment / 100;
        this.closureDate = lastPaymentC.date;
        this.excludedRepayments = excess.excludedRepayments;

        this.excludedRepaymentMessage = '';
        this.excludedRepaymentsNoteHTML = '';
        let excludedRepaymentsHTML = '<p class="notification-header">Досрочные погашения</p>';
        this.hasExcludedRepayments = excess.excludedRepayments.length > 0;
        if (this.hasExcludedRepayments) {
            this.excludedRepayments.forEach(item => {
                item.date = item.date.toLocaleFormat();
                this.excludedRepaymentMessage = `Досрочное погашение ${item.date} на ${tools.getLocaleSumInRubbles(item.payment / 100)} не участвует в расчёте.`;
                const noteMessage = `<p class="notification-row"><span>${item.date}</span><span>${tools.getLocaleSumInRubbles(item.payment / 100)}</span></p>`
                    + (item.name ? `<p class="notification-comment">${item.name}</p>` : '');
                excludedRepaymentsHTML += noteMessage;
            });
        }
        this.excludedRepaymentsNoteHTML += `<div class="excludedFundsColumn">${excludedRepaymentsHTML}</div>`;

        const detalization = detalizationC.map(row => {
            return {
                type: row.type,
                date: row.date.toString(),
                payment: (row.payment ? row.payment / 100 : 0),
                debt: (row.debt ? row.debt / 100 : 0),
                percents: (row.percents ? row.percents / 100 : 0),
                remains: (row.remains ? row.remains / 100 : 0),
                name: row.name,
                purpose: row.purpose
            };
        });

        this.detalization = detalization;

        this.dateFromOut = this.dateFrom;
        this.originalTerm = tools.verboseStackOfCountingValues([
            {value: Math.floor(termMonths / 12), triplets: ["год", "года", "лет"]},
            {value: termMonths % 12, triplets: ["месяц", "месяца", "месяцев"]},
        ]);
        this.finalTerm = tools.verboseStackOfCountingValues([
            {value: Math.floor(finalTerm / 12), triplets: ["год", "года", "лет"]},
            {value: finalTerm % 12, triplets: ["месяц", "месяца", "месяцев"]},
        ]);

        this.repaymentTotal = repaymentTotalC / 100;
        this.creditRateOut = this.creditRate;

        this.changingRateOut = changingRate ? calendarsC.rateChanges.filter(r => r.since).sort((a, b) => a.date.date - b.date.date).map((r, i, arr) => {
            return {rate: r.rate, from: r.since.toString()};
        }) : [];

        return true;

        function getAdditionalPaymentCalendar(dateFrom, payments, mainPayments, toCents = false) {
            const calendar = [];
            payments.forEach(p => {
                switch (p.repeat) {
                    case 'year':
                    case 'month': {
                        let currentDate = p.wholePeriod ? dateFrom : (dateFrom.distanceTo(p.dateFrom) < 1 ? dateFrom : p.dateFrom).addDays(-1);
                        const lastDate = p.wholePeriod ? mainPayments[mainPayments.length - 1].realDate : p.dateTo;

                        let day = 1;
                        let month = 1;
                        if (p.repeat == 'year') {
                            day = p.day;
                            month = p.month;
                        } else {
                            day = p.day == -1 ? 31 : p.day;
                            month = null;
                        }

                        const payDays = [];
                        let nextPayDay = self.getNextPayDay(p.onlyWorkdays, currentDate, day, month);
                        let realNextPayDay = p.onlyWorkdays ? self.getNextPayDay(false, currentDate, day, month) : nextPayDay;
                        while (nextPayDay.distanceTo(lastDate) > 0) {
                            payDays.push({nextPayDay, realNextPayDay});
                            currentDate = realNextPayDay;
                            nextPayDay = self.getNextPayDay(p.onlyWorkdays, currentDate, day, month);
                            realNextPayDay = p.onlyWorkdays ? self.getNextPayDay(false, currentDate, day, month) : nextPayDay;
                        }
                        payDays.forEach(pd => {
                            calendar.push({type: p.type, date: pd.nextPayDay, realDate: pd.realNextPayDay, payment: p.payment * (toCents ? 100 : 1), name: p.name, purpose: p.purpose});
                        });
                        break;
                    }
                    case 'no': {
                        if (p.onlyWorkdays) {
                            let payDay = p.date.copy();
                            while (payDay.isDayOff())
                                payDay = payDay.addDays(1);
                            calendar.push({type: p.type, date: payDay, realDate: p.date, payment: p.payment * (toCents ? 100 : 1), name: p.name, purpose: p.purpose});
                        } else
                            calendar.push({type: p.type, date: p.date, realDate: p.date, payment: p.payment * (toCents ? 100 : 1), name: p.name, purpose: p.purpose});
                        break;
                    }
                    default: {
                        break;
                    }
                }
            });
            return calendar.sort((a, b) => b.realDate.distanceTo(a.realDate) - 1).filter(c => dateFrom.distanceTo(c.realDate) > 0);
        }

        function getRateChangesCalendar(dateFrom, rateTypeValue, creditRate, changingRate) {
            return rateTypeValue == "fixed" ? [{rate: creditRate, date: dateFrom}] : changingRate.map(r => {
                return {rate: r.rate, date: r.date};
            }).sort((a, b) => b.date.distanceTo(a.date) - 1);
        }

        function calculateShortening(isTypeAnnuity, remainingDebt, percentsCC, calculation, startingRate, remainingPayments, isNextPaymentFull) {
            if (calculation.shorteningSum) {
                if (isTypeAnnuity) {
                    const calendars = {rateChanges: [startingRate], payments: remainingPayments.map(p => {return {type: p.type, date: p.date.copy(), realDate: p.realDate.copy()};}), repayments: []};
                    const precalculation = {recalculate: false, debt: 0, payment: calculation.payment, remaininigMonths: calculation.remaininigMonths, shorteningSum: 0};
                    calculatePayments(remainingDebt, calendars, calculation.remaininigMonths, isTypeAnnuity, isNextPaymentFull, precalculation, percentsCC);
                    calculation.remaininigMonths = calendars.payments.length;
                    calculation.shorteningSum = 0;
                } else {
                    calculation.remaininigMonths -= Math.floor(calculation.shorteningSum / calculation.debt);
                    calculation.shorteningSum = 0;
                }
            }
        }

        function calculateRepayment(isTypeAnnuity, remainingDebt, percentsCC, repayment, calculation, currentRate, remainingPayments, isNextPaymentFull) {
            let isRepaymentExcess = false;
            let isClosure = false;
            let lastRepaymentC = 0;
            if (repayment.purpose == "sum")
                calculateShortening(isTypeAnnuity, remainingDebt, percentsCC, calculation, {rate: currentRate.rate, date: repayment.realDate}, remainingPayments, isNextPaymentFull);

            let remainingPercentsCC = percentsCC;
            if (repayment.payment * 100 > percentsCC) {
                const percents = Math.round(percentsCC / 100);
                remainingPercentsCC = 0;
                const debtPayment = repayment.payment - percents;
                repayment.percents = percents;

                //an early finish check
                if (debtPayment < remainingDebt) {
                    repayment.debt = debtPayment;
                    repayment.remains = remainingDebt - debtPayment;
                } else {
                    isClosure = true;
                    if (debtPayment > remainingDebt) {
                        isRepaymentExcess = true;
                        lastRepaymentC = repayment.payment;
                        repayment.payment = remainingDebt + repayment.percents;
                    }
                    repayment.debt = remainingDebt;
                    repayment.remains = 0;
                }
            } else {
                remainingPercentsCC = percentsCC - repayment.payment * 100;
                repayment.percents = repayment.payment;
                repayment.debt = 0;
                repayment.remains = remainingDebt;
            }

            if (repayment.remains) {
                if (repayment.purpose == "sum") {
                    recalculateBasePayments(isTypeAnnuity, repayment.remains, calculation, currentRate);
                } else {
                    calculation.shorteningSum += repayment.debt;
                }
            }

            return {
                remainingPercentsCC, excess: {isRepaymentExcess, isClosure, lastRepaymentC, excludedRepayments: []}
            };
        }

        function recalculateBasePayments(isTypeAnnuity, remainingDebt, calculation, currentRate) {
            if (isTypeAnnuity) {
                const rateAnnu = currentRate.rate / 1200;
                calculation.payment = rateAnnu ? Math.round(remainingDebt * (rateAnnu / (1 - Math.pow((1 + rateAnnu), (-calculation.remaininigMonths))))) : Math.round(remainingDebt / calculation.remaininigMonths);
            } else {
                calculation.debt = Math.round(remainingDebt / calculation.remaininigMonths);
            }
            calculation.recalculate = false;
        }

        /**
         * Calculates debt, percents, payment and remains for every payment in calendars.payment, write it into calendars.payments and counts full term
         * @param {Number} creditSum
         * @param {Object} calendars - should contain payments, repayments and rateChanges calendars
         * @param {Number} termMonths - initial full term in months
         * @param {Boolean} isTypeAnnuity - true if annuity, false if differential
         * @param {Boolean} isFullFp - true if the first payment contains percents and debt repayments, false if the first payment contains only percents
         * @returns {Number} - recalculated full term in months
         */
        function calculatePayments(creditSum, calendars, termMonths, isTypeAnnuity, isFullFp, precalculation, startingPercentsCC = 0) {
            let excess = {isRepaymentExcess: false, isClosure: false, lastRepaymentC: 0, excludedRepayments: []};
            let remainingDebt = creditSum;
            const calculation = precalculation || {recalculate: true, debt: 0, payment: 0, remaininigMonths: termMonths, shorteningSum: 0};
            let remainingRateChanges = [...calendars.rateChanges];
            let remainingRepayments = [...calendars.repayments];
            let currentRate = remainingRateChanges.shift();
            let dateForRate = currentRate.date.addDays(1);
            currentRate.since = dateForRate;

            //initially calculate base payments
            if (calculation.recalculate)
                recalculateBasePayments(isTypeAnnuity, remainingDebt, calculation, currentRate);

            let num = 0;
            for (; num < calendars.payments.length && remainingDebt > 0; num++) {
                const paymentDate = calendars.payments[num].realDate;

                //preparations for calculating percents
                let prevRateChanges = remainingRateChanges.filter(r => r.date.distanceTo(dateForRate) > 1);
                remainingRateChanges = remainingRateChanges.slice(prevRateChanges.length);
                const newRate = prevRateChanges.pop();
                if (newRate) {
                    currentRate = newRate;
                    currentRate.since = dateForRate;
                    recalculateBasePayments(isTypeAnnuity, remainingDebt, calculation, currentRate);
                }

                let percentsCC = num ? 0 : startingPercentsCC;
                const years = paymentDate.date.getFullYear() - dateForRate.date.getFullYear();
                for (let k = 0; k <= years; k++) {
                    const year = dateForRate.date.getFullYear() + k;
                    const lastDay = k == years ? paymentDate : new DatePoint("31.12." + year);
                    const daysInYear = lastDay.date.getFullYear() % 4 ? 365 : 366;

                    //get repayments for the interval
                    const intervalRepayments = remainingRepayments.filter(r => r.realDate.distanceTo(lastDay) > 1);
                    remainingRepayments = remainingRepayments.slice(intervalRepayments.length);

                    for (let j = 0; j <= intervalRepayments.length && remainingDebt > 0; j++) {
                        const repayment = intervalRepayments[j];
                        const breakDate = repayment ? intervalRepayments[j].realDate : lastDay;

                        //calculate percents
                        const daysToPay = dateForRate.distanceTo(breakDate);
                        percentsCC += Math.round(remainingDebt * currentRate.rate * daysToPay / daysInYear);
                        dateForRate = breakDate.addDays(1);

                        //repayments before the main payment
                        if (repayment) {
                            const repaymentBefore = calculateRepayment(isTypeAnnuity, remainingDebt, percentsCC, repayment, calculation, currentRate, calendars.payments.slice(num), isFullFp || num);
                            remainingDebt = repayment.remains;
                            percentsCC = repaymentBefore.remainingPercentsCC;
                            excess = repaymentBefore.excess;
                        }
                    }
                }

                //calculate payment
                const percents = Math.round(percentsCC / 100);
                calendars.payments[num].percents = percents;
                if (isTypeAnnuity) {
                    if (!isFullFp && !num) {
                        calendars.payments[num].debt = 0;
                        calendars.payments[num].payment = percents;
                        calendars.payments[num].remains = remainingDebt;
                    } else {
                        let payment = Math.max(calculation.payment, percents);
                        let debt = payment - percents;

                        //check for an early finish
                        if (remainingDebt < debt) {
                            debt = remainingDebt;
                            payment = percents + debt;
                            if (num < calendars.payments.length - 1)
                                excess.isClosure = true;
                        }

                        remainingDebt -= debt;
                        calendars.payments[num].debt = debt;
                        calendars.payments[num].payment = payment;
                        calendars.payments[num].remains = remainingDebt;

                        calculation.remaininigMonths -= 1;
                    }
                } else {
                    if (!isFullFp && !num) {
                        calendars.payments[num].debt = 0;
                        calendars.payments[num].payment = percents;
                        calendars.payments[num].remains = remainingDebt;
                    } else {
                        //check for an early finish
                        if (remainingDebt < calculation.debt && num < calendars.payments.length - 1) {
                            excess.isClosure = true;
                        }
                        const debt = Math.min(calculation.debt, remainingDebt);

                        remainingDebt -= debt;
                        calendars.payments[num].debt = debt;
                        calendars.payments[num].payment = percents + debt;
                        calendars.payments[num].remains = remainingDebt;

                        calculation.remaininigMonths -= 1;
                    }
                }

                //pay a rounding error
                if (num == calendars.payments.length - 1 && remainingDebt > 0) {
                    calendars.payments[num].debt += remainingDebt;
                    calendars.payments[num].payment += remainingDebt;
                    calendars.payments[num].remains = 0;
                    remainingDebt = 0;
                }

                //get repayments for the interval
                const intervalRepayments = remainingRepayments.filter(r => r.realDate.distanceTo(paymentDate) > 0);
                remainingRepayments = remainingRepayments.slice(intervalRepayments.length);

                for (let j = 0; j <= intervalRepayments.length && remainingDebt > 0; j++) {
                    const repayment = intervalRepayments[j];

                    //repayments after the main payment
                    if (repayment) {
                        const repaymentBefore = calculateRepayment(isTypeAnnuity, remainingDebt, 0, repayment, calculation, currentRate, calendars.payments.slice(num + 1), isFullFp || num);
                        remainingDebt = repayment.remains;
                        excess = repaymentBefore.excess;
                    }
                }
            }

            //remove excess payments after an early finish
            excess.excludedRepayments.push(...calendars.repayments.filter(r => r.remains == null));
            calendars.payments = calendars.payments.filter(p => p.remains != null);
            calendars.repayments = calendars.repayments.filter(r => r.remains != null);

            return {finalTerm: num, excess};
        }
    }

    getIsFullMonth(dateFrom, frequencyMonth) {
        let payDay = 1;
        switch (frequencyMonth.selected) {
            case 0: {
                payDay = dateFrom.date.getDate();
                break;
            }
            case 1: {
                payDay = 31;
                break;
            }
            case 2: {
                payDay = frequencyMonth.day;
                break;
            }
        }
        const nextPayDay = this.getNextPayDay(false, dateFrom, payDay);
        return (dateFrom.date.getFullYear() * 12 + dateFrom.date.getMonth()) < (nextPayDay.date.getFullYear() * 12 + nextPayDay.date.getMonth());
    }

    getPaymentDateCalendar(dateFrom, termMonths, frequencyMonth, isFullFp, isFullMonth, onlyWorkdays) {
        const calendar = [];
        let currentDate = dateFrom;

        const timesToPay = termMonths + (isFullFp ? 0 : 1);
        let payDay = 1;
        switch (frequencyMonth.selected) {
            case 0: {
                payDay = dateFrom.date.getDate();
                break;
            }
            case 1: {
                payDay = 31;
                break;
            }
            case 2: {
                payDay = frequencyMonth.day;
                break;
            }
        }
        for (let i = 0; i < timesToPay; i++) {
            const nextPayDay = this.getNextPayDay(onlyWorkdays, currentDate, payDay);
            const realNextPayDay = onlyWorkdays ? this.getNextPayDay(false, currentDate, payDay) : nextPayDay;
            currentDate = realNextPayDay;
            calendar.push({type: 'payment', date: nextPayDay, realDate: realNextPayDay});
        }
        if (!isFullMonth)
            calendar[0].date = new DatePoint(calendar[1].date);
        return calendar;
    }

    //Get next date available for
    //months = 1 - 12
    getNextPayDay(onlyWorkdays, today, payDay, payMonth) {
        let nextPayDay = null;
        const todayDay = today.date.getDate();
        const todayMonth = today.date.getMonth() + 1;
        const todayYear = today.date.getFullYear();
        if (payMonth != null) {
            const isGapDay = payDay == 29 && payMonth == 2;
            const currentYearPayDay = isGapDay && fixDayMonthYear(today, 1, 2, todayYear).getDaysInMonth() == 28 ? fixDayMonthYear(today, 28, 2) :
                fixDayMonthYear(today, payDay, payMonth);
            nextPayDay = currentYearPayDay.distanceTo(today) < 1 ? currentYearPayDay :
                isGapDay && fixDayMonthYear(today, 1, 2, todayYear + 1).getDaysInMonth() == 28 ? fixDayMonthYear(today, 28, 2, todayYear + 1) :
                    fixDayMonthYear(today, payDay, payMonth, todayYear + 1);
        } else {
            const currentMonthPayDay = Math.min(today.getDaysInMonth(), payDay);
            if (currentMonthPayDay > todayDay)
                nextPayDay = fixDayMonthYear(today, currentMonthPayDay);

            else if (todayMonth == 12)
                nextPayDay = fixDayMonthYear(today, payDay, 1, todayYear + 1);
            else {
                const nextMonthPayDay = Math.min(fixDayMonthYear(today, 1, todayMonth + 1).getDaysInMonth(), payDay);
                nextPayDay = fixDayMonthYear(today, nextMonthPayDay, todayMonth + 1);
            }
        }

        if (onlyWorkdays)
            while (nextPayDay.isDayOff())
                nextPayDay = nextPayDay.addDays(1);

        return nextPayDay;

        function fixDayMonthYear(datepoint, day, month, year) {
            const initialDay = datepoint.date.getDate();
            return datepoint.fixDayMonthYear(1).fixDayMonthYear(day || initialDay, month, year);
        }
    }

    // TODO: Add a connection for dynamic fields between a parent's ignore and children's instead of this
    setDynamicInputIgnore(inputName, ignore) {
        this.inputFields.filter(f => !f.name.indexOf(inputName + "[]")).forEach(f => f.ignore = ignore);
    }
}
export {BaseCreditCalculator};
